import React, { useState, useEffect } from 'react'
import { getCrew, addCrewMember, deleteCrewMember, toggleCrewMember, setCrewColor, renameCrewMember } from '../lib/crew'
import { CREW_COLOR_PRESETS, isValidHexColor } from '../lib/crewColors'

function ColorPickerPopover({ value, onSelect, onClose }) {
  const [customMode, setCustomMode] = useState(false)
  const [customHex, setCustomHex] = useState(value && !CREW_COLOR_PRESETS.some(p => p.hex === value) ? value : '#3b82f6')

  const handleCustomSubmit = () => {
    if (isValidHexColor(customHex)) {
      onSelect(customHex)
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-sm p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {customMode ? 'Custom color' : 'Pick a color'}
          </span>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" title="Close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

      {!customMode ? (
        <>
          <div className="grid grid-cols-5 gap-2">
            {CREW_COLOR_PRESETS.map(preset => (
              <button
                key={preset.hex}
                onClick={() => { onSelect(preset.hex); onClose() }}
                title={preset.name}
                className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${
                  value === preset.hex ? 'border-gray-900' : 'border-white shadow'
                }`}
                style={{ backgroundColor: preset.hex }}
              />
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={() => setCustomMode(true)}
              className="text-xs text-brand-600 hover:text-brand-700 font-medium"
            >
              Custom color…
            </button>
            {value && (
              <button
                onClick={() => { onSelect(null); onClose() }}
                className="text-xs text-gray-500 hover:text-red-600 font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={isValidHexColor(customHex) ? customHex : '#3b82f6'}
              onChange={(e) => setCustomHex(e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              placeholder="#3b82f6"
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="mt-3 flex justify-between items-center">
            <button
              onClick={() => setCustomMode(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
            <button
              onClick={handleCustomSubmit}
              disabled={!isValidHexColor(customHex)}
              className="px-3 py-1.5 text-xs font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  )
}

export default function Crew() {
  const [crew, setCrew] = useState([])
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [pickerOpenId, setPickerOpenId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editError, setEditError] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadCrew()
  }, [])

  const loadCrew = async () => {
    setLoading(true)
    const data = await getCrew()
    setCrew(data)
    setLoading(false)
  }

  const handleAdd = async () => {
    const trimmed = newName.trim()
    if (!trimmed) {
      setError('Name is required.')
      return
    }
    if (crew.some(c => c.name.toLowerCase() === trimmed.toLowerCase())) {
      setError('A crew member with that name already exists.')
      return
    }
    try {
      await addCrewMember(trimmed)
      await loadCrew()
      setNewName('')
      setAdding(false)
      setError('')
    } catch (err) {
      setError('Failed to add crew member. Please try again.')
      console.error(err)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteCrewMember(id)
      await loadCrew()
      setConfirmDelete(null)
    } catch (err) {
      console.error('Failed to delete crew member:', err)
    }
  }

  const handleToggle = async (id) => {
    try {
      await toggleCrewMember(id)
      await loadCrew()
    } catch (err) {
      console.error('Failed to toggle crew member:', err)
    }
  }

  const handleColorChange = async (id, color) => {
    try {
      const updated = await setCrewColor(id, color)
      setCrew(updated)
    } catch (err) {
      console.error('Failed to set crew color:', err)
    }
  }

  const handleStartEdit = (member) => {
    setEditingId(member.id)
    setEditName(member.name)
    setEditError('')
    setEditSaving(false)
    setConfirmDelete(null)
    setPickerOpenId(null)
  }

  const handleSaveEdit = async (id) => {
    if (editSaving) return
    const trimmed = editName.trim()
    if (!trimmed) {
      setEditError('Name is required.')
      return
    }
    const current = crew.find(c => c.id === id)
    if (current && trimmed === current.name) {
      // No change — just close
      handleCancelEdit()
      return
    }
    if (crew.some(c => c.id !== id && c.name.toLowerCase() === trimmed.toLowerCase())) {
      setEditError('A crew member with that name already exists.')
      return
    }
    try {
      setEditSaving(true)
      await renameCrewMember(id, trimmed)
      await loadCrew()
      setEditingId(null)
      setEditName('')
      setEditError('')
      setEditSaving(false)
    } catch (err) {
      setEditError('Failed to save. Please try again.')
      setEditSaving(false)
      console.error(err)
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditName('')
    setEditError('')
  }

  const handleCancelAdd = () => {
    setAdding(false)
    setNewName('')
    setError('')
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crew Members</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Active crew appear as columns in the calendar. Click the color swatch to assign a calendar color.
          </p>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Crew Member
          </button>
        )}
      </div>

      {adding && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">New Crew Member</h2>
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <input
                type="text"
                autoFocus
                value={newName}
                onChange={e => { setNewName(e.target.value); setError('') }}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') handleCancelAdd() }}
                placeholder="e.g. John D"
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 ${error ? 'border-red-400' : 'border-gray-300'}`}
              />
              {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            </div>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
            >
              Add
            </button>
            <button
              onClick={handleCancelAdd}
              className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="px-6 py-12 text-center text-gray-400 text-sm">
            Loading crew members...
          </div>
        ) : crew.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-400 text-sm">
            No crew members yet. Add one above.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {crew.map(member => (
              <li key={member.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${member.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {editingId === member.id ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          autoFocus
                          value={editName}
                          onChange={e => { setEditName(e.target.value); setEditError('') }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveEdit(member.id)
                            if (e.key === 'Escape') handleCancelEdit()
                          }}
                          disabled={editSaving}
                          className={`w-full px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-60 ${editError ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {editError && <p className="text-xs text-red-500 mt-0.5">{editError}</p>}
                      </div>
                      <button
                        onClick={() => handleSaveEdit(member.id)}
                        disabled={editSaving}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors flex-shrink-0 disabled:opacity-50"
                        title="Save"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                        title="Cancel"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={`text-sm font-medium ${member.active ? 'text-gray-900' : 'text-gray-400'}`}>
                        {member.name}
                      </span>
                      {!member.active && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                    </>
                  )}
                </div>

                {editingId !== member.id && (
                  <div className="flex items-center gap-2 relative flex-shrink-0">
                    <button
                      onClick={() => setPickerOpenId(pickerOpenId === member.id ? null : member.id)}
                      className="w-7 h-7 rounded-full border-2 border-gray-200 hover:border-gray-400 transition-colors flex items-center justify-center"
                      style={{ backgroundColor: member.color || 'transparent' }}
                      title={member.color ? `Color: ${member.color}` : 'No color assigned'}
                    >
                      {!member.color && (
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                        </svg>
                      )}
                    </button>
                    {pickerOpenId === member.id && (
                      <ColorPickerPopover
                        value={member.color}
                        onSelect={(color) => handleColorChange(member.id, color)}
                        onClose={() => setPickerOpenId(null)}
                      />
                    )}

                    <button
                      onClick={() => handleToggle(member.id)}
                      className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                        member.active
                          ? 'text-gray-600 border border-gray-300 hover:bg-gray-100'
                          : 'text-brand-600 border border-brand-300 hover:bg-brand-50'
                      }`}
                    >
                      {member.active ? 'Deactivate' : 'Activate'}
                    </button>

                    <button
                      onClick={() => handleStartEdit(member)}
                      className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                      title="Edit name"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>

                    {confirmDelete === member.id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500">Sure?</span>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-xs px-2.5 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs px-2.5 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(member.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-4 text-center">
        {crew.filter(c => c.active).length} of {crew.length} crew members active
      </p>
      </div>
    </div>
  )
}
